import { useCallback, useContext, useEffect, useRef } from 'react';

import { animateDuration } from '@/components/Lottery/config';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  addLotteryWinner,
  addMyUnclaimedTickets,
  changeClaimLotteryRewardsStatus,
  endLottery,
  setFirstTicketReward,
} from '@/redux/reducers/lotteryReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { WebsocketEmitters } from '../app/socket';
import { socket } from '../socket/socket';
import {
  type LotteryWinnerInfo,
  type MyLotteryTicketInfo,
} from '../types/lottery';

const useLotteryWinnersListener = (): void => {
  const myUserId = useAppSelector((state) => state.user.user?.id);
  const dispatch = useAppDispatch();
  const { isOpen, openModal, secondModal, thirdModal } =
    useContext(ModalContext);
  const timerId = useRef<NodeJS.Timeout>();

  const handleWinnerListener = useCallback(
    (data: LotteryWinnerInfo) => {
      if (myUserId === data.userId) {
        const { id, ticket, drawResult, drawRank } = data;
        dispatch(addMyUnclaimedTickets({ id, ticket, drawRank, drawResult }));
      }

      dispatch(addLotteryWinner(data));

      if (data.drawRank === 1) {
        timerId.current = setTimeout(
          () => dispatch(endLottery()),
          (animateDuration + 2) * 1000,
        );
      }
    },
    [myUserId],
  );

  const handleIncentiveReward = useCallback((data: LotteryWinnerInfo) => {
    const { id, ticket, drawResult, drawRank } = data;
    dispatch(addMyUnclaimedTickets({ id, ticket, drawRank, drawResult }));
    dispatch(changeClaimLotteryRewardsStatus(true));
  }, []);

  const handleFirstLotteryTicket = useCallback(
    (data: MyLotteryTicketInfo) => {
      dispatch(setFirstTicketReward(data));
    },
    [isOpen, openModal, secondModal, thirdModal],
  );

  useEffect(() => {
    socket.on(WebsocketEmitters.LOTTERY_TICKET_DRAWN, handleWinnerListener);
    socket.on(WebsocketEmitters.LOTTERY_INCENTIVE, handleIncentiveReward);
    socket.on(WebsocketEmitters.FIRST_LOTTERY_TICKET, handleFirstLotteryTicket);

    return (): void => {
      clearTimeout(timerId.current);
      socket.off(WebsocketEmitters.LOTTERY_TICKET_DRAWN, handleWinnerListener);
      socket.off(WebsocketEmitters.LOTTERY_INCENTIVE, handleIncentiveReward);
      socket.off(
        WebsocketEmitters.FIRST_LOTTERY_TICKET,
        handleFirstLotteryTicket,
      );
    };
  }, [handleWinnerListener, handleIncentiveReward, handleFirstLotteryTicket]);
};

export { useLotteryWinnersListener };
